/**
 * deriveBrowseMeta, Sprint 1.6 deterministic per-SKU metadata for
 * the browse picker's grid cards.
 *
 * Why deterministic (vs the LLM-chosen meta the chat widget uses):
 *   The browse picker doesn't run an LLM per card render, that would
 *   be a per-pageload cost explosion. So this util picks 0–2 meta chips
 *   per SKU based on its category + populated fields. The shape mirrors
 *   what the chat widget emits ({label, value}[]) so the same SkuCard
 *   component renders both surfaces identically.
 *
 * The rules below are an MVP, tune as we learn what customers actually
 * scan for. Eventually some surfaces may want LLM-chosen meta even on
 * the browse picker (e.g. "show me how this matches Sarah's profile"),
 * but that's Phase 2.
 */

export interface BrowseMetaInput {
  vendor_category: string;
  lead_time_days?: number | null;
  is_voucher?: boolean;
  dietary_tags?: string[] | null;
  description?: string | null;
}

export interface MetaPair {
  label: string;
  value: string;
}

function formatLeadTime(days: number): string {
  if (days <= 1) return "Same/next day";
  if (days < 7) return `${days} days`;
  if (days < 14) return "About a week";
  return `${Math.round(days / 7)} weeks`;
}

function topDietaryTag(tags: string[]): string | null {
  // Surface one, most distinctive tags first. If a SKU is BOTH vegan
  // and gluten-free, vegan is the stronger signal so prefer it.
  const order = ["vegan", "gluten_free", "dairy_free", "vegetarian", "halal", "kosher", "nut_free"];
  for (const t of order) if (tags.includes(t)) return t;
  return tags[0] ?? null;
}

const TAG_LABEL: Record<string, string> = {
  vegan: "Vegan",
  vegetarian: "Vegetarian",
  gluten_free: "Gluten-free",
  dairy_free: "Dairy-free",
  halal: "Halal",
  kosher: "Kosher",
  nut_free: "Nut-free",
};

export function deriveBrowseMeta(sku: BrowseMetaInput): MetaPair[] {
  const out: MetaPair[] = [];

  // Vouchers, surface the validity + treatment when possible. We
  // don't have a structured "validity" column today, so we lean on
  // a generic "Voucher" indicator. The chat widget can surface
  // richer meta because the LLM reads the description.
  if (sku.is_voucher) {
    out.push({ label: "Format", value: "Digital voucher" });
  }

  // Lead time when present and non-zero. Same-day vendors don't need
  // to advertise it; the absence of a lead time chip implies fast.
  if (typeof sku.lead_time_days === "number" && sku.lead_time_days > 0) {
    out.push({ label: "Lead time", value: formatLeadTime(sku.lead_time_days) });
  }

  // Dietary tag when present (consumables only, cake / hamper / meal /
  // coffee). Cap at one chip so the card stays scannable.
  // MS454: coffee was missing — dietary_tags on coffee SKUs (e.g.
  // "dairy-free options", "oat milk available") were silently dropped.
  // Coffee differs from cake/hamper/meal in that the recipient redeems
  // a voucher, but dietary tags on the browse card still communicate
  // available options (vegan milk, decaf, etc.) so including it here
  // is correct. The allergy-capture path (FilterRail / selector) keeps
  // coffee excluded separately because the buyer doesn't pre-select for
  // the recipient — that's a distinct concern.
  const isConsumable =
    sku.vendor_category === "cake" ||
    sku.vendor_category === "hamper" ||
    sku.vendor_category === "meal" ||
    sku.vendor_category === "coffee";
  if (isConsumable && Array.isArray(sku.dietary_tags) && sku.dietary_tags.length > 0) {
    const top = topDietaryTag(sku.dietary_tags);
    if (top) out.push({ label: "Dietary", value: TAG_LABEL[top] ?? top });
  }

  // Cap at 2, keeps cards visually quiet. Lead time + dietary is the
  // most informative pair for consumables; format + lead time for
  // vouchers; lead time alone for everything else.
  return out.slice(0, 2);
}
